import { apiClient } from './apiClient';
import { logger } from '../../utils/logger';
import type {
  Journey,
  JourneyWeek,
  JourneyActivity,
  ActivityProgress,
  CreateJourneyInput,
  CompletionInput,
  JourneyStats,
  SkillMastery,
} from '@coffeeplexity/shared';

export const journeyApi = {
  // Get user's journeys
  getMyJourneys: async (): Promise<Journey[]> => {
    const response = await apiClient.get('/journeys/', {
      params: {
        include_weeks: true,
        include_activities: true,
        limit: 50,
      },
    });
    // API returns a wrapped list: { journeys: Journey[], total, limit, offset } or { items: Journey[] }
    const data = response.data as any;
    logger.debug('API Response:', data);
    return Array.isArray(data) ? data : (data.journeys ?? data.items ?? []);
  },

  // Get specific journey
  getJourney: async (journeyId: string): Promise<Journey> => {
    const response = await apiClient.get(`/journeys/${journeyId}`);
    return response.data;
  },

  // Get journey week
  getJourneyWeek: async (
    journeyId: string,
    weekNumber: number
  ): Promise<JourneyWeek> => {
    const response = await apiClient.get(
      `/journeys/${journeyId}/weeks/${weekNumber}`
    );
    return response.data;
  },

  // Get journey activity
  getJourneyActivity: async (activityId: string): Promise<JourneyActivity> => {
    const response = await apiClient.get(`/journeys/activities/${activityId}`);
    return response.data;
  },

  // Create new journey
  createJourney: async (input: CreateJourneyInput): Promise<Journey> => {
    const response = await apiClient.post('/journeys/', input);
    return response.data;
  },

  // Complete activity
  completeActivity: async (
    activityId: string,
    payload: CompletionInput
  ): Promise<ActivityProgress> => {
    const response = await apiClient.post(
      `/journeys/activities/${activityId}/complete`,
      payload
    );
    return response.data;
  },

  // Update activity progress
  updateActivityProgress: async (
    activityId: string,
    progressPercentage: number
  ): Promise<ActivityProgress> => {
    const response = await apiClient.patch(
      `/journeys/activities/${activityId}/progress`,
      {
        progress_percentage: progressPercentage,
      }
    );
    return response.data;
  },

  // Rate activity
  rateActivity: async (
    activityId: string,
    rating: number,
    feedback?: string
  ): Promise<void> => {
    await apiClient.post(`/journeys/activities/${activityId}/rate`, {
      rating,
      feedback,
    });
  },

  // Adapt journey
  adaptJourney: async (journeyId: string): Promise<Journey> => {
    const response = await apiClient.post(`/journeys/${journeyId}/adapt`);
    return response.data;
  },

  // Get journey stats
  getJourneyStats: async (journeyId: string): Promise<JourneyStats> => {
    const response = await apiClient.get(`/journeys/${journeyId}/stats`);
    return response.data;
  },

  // Get user's skill mastery
  getSkillMastery: async (journeyId: string): Promise<SkillMastery[]> => {
    const response = await apiClient.get(`/journeys/${journeyId}/mastery`);
    return response.data;
  },

  // Get activity recommendations
  getRecommendations: async (
    journeyId: string,
    limit: number = 5
  ): Promise<JourneyActivity[]> => {
    const response = await apiClient.get(
      `/journeys/${journeyId}/recommendations`,
      {
        params: { limit },
      }
    );
    return response.data;
  },

  // Pause/resume journey
  updateJourneyStatus: async (
    journeyId: string,
    status: 'active' | 'paused'
  ): Promise<Journey> => {
    const response = await apiClient.patch(`/journeys/${journeyId}/status`, {
      status,
    });
    return response.data;
  },
};
